import { memo, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import type { Message, Artifact, CustomDataArtifact } from '@dev-streetbeat/chat';
import { Tabs, TabsList, TabsTrigger } from 'shared/components/ui/tabs';
import activityPanelStore from 'shared/store/activityPanelStore';
import { FinecoPortfolio } from './single/FinecoPortfolio';
import { FinecoPortfolioCompare } from './compare/FinecoPortfolioCompare';

type Props = {
  message: Message;
  artifacts: Artifact[];
  finecoPortfolioAndPortfolioStoryTogether: boolean;
};

export const RebalancingTabbedView: React.FC<Props> = memo(({ message, artifacts, finecoPortfolioAndPortfolioStoryTogether }) => {
  const { t } = useTranslation();
  const params = useParams();
  const threadId = params.threadId;
  const setFinecoPortfolioArtifact = activityPanelStore((state) => state.setFinecoPortfolioArtifact);
  const [activeTab, setActiveTab] = useState('portfolio');

  const portfolioArtifact = useMemo(() => {
    return artifacts.find(
      (a) =>
        a.type === 'custom_data' &&
        (a as CustomDataArtifact).custom_data?.type === 'fineco_portfolio' &&
        (a as CustomDataArtifact).custom_data?.data != null,
    ) as CustomDataArtifact | undefined;
  }, [artifacts]);

  const comparisonArtifact = useMemo(() => {
    return artifacts.find(
      (a) =>
        a.type === 'custom_data' &&
        (a as CustomDataArtifact).custom_data?.type === 'fineco_portfolio_comparison' &&
        (a as CustomDataArtifact).custom_data?.data != null,
    ) as CustomDataArtifact | undefined;
  }, [artifacts]);

  // Replicate the activity panel side-effect from MessageArtifact
  useEffect(() => {
    if (portfolioArtifact) {
      setFinecoPortfolioArtifact(threadId, portfolioArtifact);
    }
  }, [portfolioArtifact, threadId, setFinecoPortfolioArtifact]);

  if (!portfolioArtifact || !comparisonArtifact) {
    return null;
  }

  const portfolioData = portfolioArtifact.custom_data.data as any;
  const portfolioId = portfolioData?.portfolio_id;

  // Styles: active tab is normal flow; inactive tab is kept in the render tree
  // (visibility:hidden + position:absolute) so Section scrollHeight measurements work,
  // but it takes no visual space and is invisible to the user.
  const visibleStyle: React.CSSProperties = {};
  const hiddenStyle: React.CSSProperties = {
    visibility: 'hidden',
    position: 'absolute',
    top: 0,
    width: '100%',
    pointerEvents: 'none',
  };

  return (
    <div style={{ width: '100%', position: 'relative', overflow: 'hidden' }}>
      <Tabs defaultValue="portfolio" onValueChange={setActiveTab}>
        <TabsList className="mb-4 w-full">
          <TabsTrigger value="portfolio">{t('rebalancing.portfolioTab', 'Portafoglio')}</TabsTrigger>
          <TabsTrigger value="comparison">{t('rebalancing.comparisonTab', 'Confronto')}</TabsTrigger>
        </TabsList>
      </Tabs>
      <div style={activeTab === 'portfolio' ? visibleStyle : hiddenStyle}>
        <div id={portfolioId?.toString()} className="fineco-portfolio-scroll-margin"></div>
        <FinecoPortfolio
          message={{ ...message, thread_id: threadId || '' }}
          finecoPortfolioAndPortfolioStoryTogether={finecoPortfolioAndPortfolioStoryTogether}
          artifact={portfolioArtifact}
        />
      </div>
      <div style={activeTab === 'comparison' ? visibleStyle : hiddenStyle}>
        <FinecoPortfolioCompare
          data={comparisonArtifact.custom_data.data}
          artifact={comparisonArtifact}
        />
      </div>
    </div>
  );
});

RebalancingTabbedView.displayName = 'RebalancingTabbedView';
